/* GrowthOS — Create/Edit Contact page.
   Same shell as addLead.jsx (sticky header, cover with identity + readiness card,
   config-driven form sections below via contactForm.jsx) — just the fields/required
   set match Contact instead of Lead, incl. the same photo-upload widget. The image
   APIs are named *LeadImageCRM but are generic Azure-blob helpers keyed by a
   `${module}/${id}` path, not Lead-specific — reused here with module 'Contact'.
   Mirrors Lead exactly: "Remove" only clears local state (Lead never calls a delete
   API either — deleteLeadImageCRM is defined but unused everywhere). */
import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { initValueContact } from '../../shared/initials';
import apiServices from '../../services/service';
import { toast } from 'react-toastify';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import { fetchDataAndUpdateStore, singleRowAddUpdateInStore } from '../../redux/actions';
import ContactForm from './contactForm';
import { validationSchemaContact } from './validationSchema';
import { getGridDataByModuleKey, getUser } from '../../redux/selector';
import { dropdownPayload } from '../../shared/constants';

const CL_ICONS = {
  chevL: <path d="M15 18l-6-6 6-6" />,
  check: <path d="M20 6 9 17l-5-5" />,
  user: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  building: <path d="M3 21h18M6 21V4h9v17M9 8h3M9 12h3M9 16h3M15 21V9h4v12" />,
  campaigns: <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1ZM15 8a4 4 0 0 1 0 8M18 5a8 8 0 0 1 0 14" />,
  image: (<><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9" r="1.5" /><path d="M21 16l-5-5L5 20" /></>),
  refresh: <path d="M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5" />,
  trash: <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6h12M10 11v6M14 11v6" />,
  checkCircle: (<><circle cx="12" cy="12" r="9" /><path d="M8.5 12l2.5 2.5 4.5-5" /></>),
};
const CLIc = ({ name, size = 16, style }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {CL_ICONS[name] || null}
  </svg>
);

const REQUIRED = [
  { key: 'lastName', label: 'Last Name' },
  { key: 'leadSourceId', label: 'Lead Source' },
];

const AddContactsForm = () => {
  const gridData = useSelector(getGridDataByModuleKey('contactList'));
  const handleCloseAddForm = () => {
    if (formData.from === 'view') {
      navigate(`/contacts/viewContact?id=${formData.id}&pageNo=${gridData?.inputPayload?.pageNumber}`);
    } else {
      navigate('/contacts');
    }
  };
  const stateData = useSelector((state) => state);
  const dispatch = useDispatch();
  let storeLoading = isLoadingByKey(stateData, 'leadSourceList', 'salutation', 'contactPersonRole', 'owner');

  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useSelector(getUser);
  if (!initValueContact.owner) {
    initValueContact.owner = userInfo?.user?.display_name;
    initValueContact.ownerId = userInfo?.user?.userId;
  }
  const [loading, setLoading] = useState(storeLoading);
  const [formData, setFormData] = useState(initValueContact);
  const [errormsg, setErrorMsg] = useState({});
  const [formEdited, setFormEdited] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [imagefile, setImageFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);

  const handleUploadImage = async (imagefile) => {
    setImageFile(imagefile);
    setFormEdited(true);
  };

  // show the existing photo when editing
  useEffect(() => {
    if (formData?.id) {
      apiServices
        .getLeadImageCRM(`Contact/${formData.id}`)
        .then((url) => setPhotoPreview(url))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.id]);

  const onPhotoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
    handleUploadImage(file);
    e.target.value = '';
  };
  const removePhoto = () => {
    setPhotoPreview(null);
    setImageFile(null);
    setFormEdited(true);
  };

  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    let convertedValue = value;

    if (['phone', 'mobile', 'assistantPhone'].includes(name)) {
      convertedValue = value.replace(/[^0-9+\-() ]/g, '');
    }

    setErrorMsg((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    setFormData((prev) => ({
      ...prev,
      [name]: convertedValue,
    }));

    setFormEdited(true);
  };

  const handleChange = (selectedOption, name) => {
    setSelectedOptions((prevState) => ({
      ...prevState,
      [name]: selectedOption,
    }));

    handleLookupDropDownChange(selectedOption.value, selectedOption, name);
  };

  const handleLookupDropDownChange = (selectedId, selectedOption, name) => {
    const selectedValue = selectedOption.label;
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));
    if (name === 'leadSourceId') {
      setFormData({ ...formData, leadSourceId: selectedId, leadSource: selectedValue });
      setFormEdited(true);
    } else if (name === 'salutationId') {
      setFormData({ ...formData, salutationId: selectedId, salutation: selectedValue });
      setFormEdited(true);
    } else if (name === 'contactPersonRoleId') {
      setFormData({ ...formData, contactPersonRoleId: selectedId, contactPersonRole: selectedValue });
      setFormEdited(true);
    } else if (name === 'accountId') {
      setFormData({ ...formData, accountId: selectedId, account: selectedValue });
      setFormEdited(true);
    } else if (name === 'ownerId') {
      setFormData({ ...formData, ownerId: selectedId, owner: selectedValue });
      setFormEdited(true);
    }
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: checked,
    }));
    setFormEdited(true);
  };

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      const sanitizedData = {
        ...formData,
        facebook: formData?.facebook?.replace(/^(https?:\/\/)?(www\.)?/, ''),
        twitter: formData?.twitter?.replace(/^(https?:\/\/)?(www\.)?/, ''),
      };

      await validationSchemaContact.validate(sanitizedData, { abortEarly: false });
      handleFormSubmit(sanitizedData);
    } catch (error) {
      setLoading(false);
      const fieldErrors = {};
      error?.inner?.forEach((err) => {
        fieldErrors[err.path] = err.message;
      });
      setErrorMsg(fieldErrors);
      const errorFields = Object.keys(fieldErrors);
      if (errorFields.length > 0) {
        const firstErrorKey = errorFields[0];
        const errorRef = refs[firstErrorKey];
        if (errorRef?.current) {
          errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          errorRef.current.focus();
        }
      }
      console.error('Validation Error:', fieldErrors);
    }
  };
  const refs = {
    lastName: useRef(null),
    leadSourceId: useRef(null),
  };

  const updateList = async (msg, id) => {
    setLoading(false);
    toast.success(msg, { position: 'top-right' });

    try {
      dispatch(
        singleRowAddUpdateInStore({
          moduleName: 'Contact',
          id: formData.type === 'add' ? id : formData.id,
          rowIndex: formData.rowIndex,
        })
      );
      dispatch(
        fetchDataAndUpdateStore({
          key: 'contactDropdown',
          inputData: dropdownPayload,
        })
      );
      if (formData.from === 'view') {
        navigate(`/contacts/viewContact?id=${formData.id}&pageNo=${gridData?.inputPayload?.pageNumber}`);
      } else {
        navigate('/contacts');
      }
    } catch (error) {
      console.error('Failed to fetch and update Contact:', error);
      toast.error('Something went wrong while updating the list.', { position: 'top-right' });
    }
  };

  const saveUploadImage = async (id) => {
    if (imagefile && id) {
      const newFile = new File([imagefile], id.data, {
        type: imagefile.type,
      });
      await apiServices.uploadLeadImageCRM(newFile, 'Contact');
    }
  };

  const editUploadImage = async (id) => {
    if (imagefile && id) {
      const newFile = new File([imagefile], id, {
        type: imagefile.type,
      });
      await apiServices.uploadLeadImageCRM(newFile, 'Contact');
    }
  };

  const handleFormSubmit = (formData) => {
    if (formData.type === 'add') {
      apiServices
        .createContactList(formData)
        .then((response) => {
          if (response.status === 200) {
            const id = response.data;
            saveUploadImage(response);
            updateList('Contact created successfully!', id);
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })
        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);
          toast.error('An unexpected error occurred.', { position: 'top-right' });
          setLoading(false);
        });
    } else {
      editUploadImage(formData.id);
      apiServices
        .updateContactList(formData)
        .then((response) => {
          if (response.status === 200) {
            updateList('Contact updated successfully!');
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })
        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);
          toast.error('An unexpected error occurred.', { position: 'top-right' });
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    setLoading(storeLoading);
  }, [storeLoading]);

  useEffect(() => {
    let updatedFormData = {
      ...formData,
      type: location.pathname.includes('addContact') ? 'add' : 'edit',
    };
    if (location.pathname.includes('editContact') && location.state) {
      updatedFormData = {
        ...location.state,
        type: 'edit',
      };
    }
    setFormData(updatedFormData);
  }, [location]);

  const isEdit = formData?.type === 'edit';
  const fullName = [formData?.firstName, formData?.middleName, formData?.lastName].filter(Boolean).join(' ').trim();
  const displayName = fullName || (isEdit ? 'Contact' : 'Untitled contact');
  const idSub = [formData?.title, formData?.account].filter(Boolean).join(' · ');
  const filledReq = REQUIRED.filter((r) => {
    const v = formData?.[r.key];
    return v !== null && v !== undefined && String(v).trim() !== '';
  }).length;
  const pct = Math.round((filledReq / REQUIRED.length) * 100);
  const allReady = filledReq === REQUIRED.length;

  return (
    <div className="addlead_container gos-cl">
      {/* sticky header */}
      <div className="gos-cl-header">
        <button type="button" className="gos-cl-back" onClick={handleCloseAddForm} title="Back to Contacts">
          <CLIc name="chevL" size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gos-cl-htitle">{isEdit ? 'Edit Contact' : 'New Contact'}</div>
          <div className="gos-cl-hsub">
            Fields marked <span className="gos-cl-reqdot">*</span> are required
          </div>
        </div>
        <button type="button" className="gos-btn gos-btn-ghost" onClick={handleCloseAddForm}>Cancel</button>
        <button
          type="button"
          className="gos-btn gos-btn-primary"
          onClick={() => handleSubmit(formData)}
          disabled={loading || (isEdit && !formEdited)}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving…
            </>
          ) : (
            <>
              <CLIc name="check" size={16} /> Save Contact
            </>
          )}
        </button>
      </div>

      <div className="gos-cl-scroll">
        <div className="gos-cl3-body">
          {/* cover */}
          <div className="gos-cl3-cover">
            <div className="gos-cl-photo-wrap">
              <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPhotoPick} />
              <div
                className={'gos-cl-photo' + (photoPreview ? ' has-img' : '')}
                onClick={photoPreview ? undefined : () => photoInputRef.current?.click()}
              >
                {photoPreview ? (
                  <>
                    <img src={photoPreview} alt="Contact" />
                    <div className="gos-cl-photo-overlay" onClick={() => photoInputRef.current?.click()}>Change</div>
                  </>
                ) : (
                  <div className="gos-cl-photo-empty">
                    <CLIc name="image" size={24} />
                    <span>Add photo</span>
                  </div>
                )}
              </div>
              {photoPreview ? (
                <div className="gos-cl-photo-actions">
                  <button type="button" className="gos-cl-photo-act" onClick={() => photoInputRef.current?.click()}>
                    <CLIc name="refresh" size={12} /> Change
                  </button>
                  <button type="button" className="gos-cl-photo-act danger" onClick={removePhoto}>
                    <CLIc name="trash" size={12} /> Remove
                  </button>
                </div>
              ) : (
                <div className="gos-cl-photo-hint">JPG or PNG · click to upload</div>
              )}
            </div>

            <div className="gos-cl3-id">
              <div className="gos-cl3-id-eyebrow">{isEdit ? 'Edit Contact' : 'New Contact'}</div>
              <div className={'gos-cl3-id-name' + (fullName ? '' : ' ph')}>{displayName}</div>
              <div className="gos-cl3-id-sub">{idSub || 'Add contact and account details below'}</div>
              <div className="gos-cl3-id-chips">
                <span className="gos-lr-chip"><CLIc name="user" size={13} /> Owner: {formData?.owner || 'Unassigned'}</span>
                {formData?.account ? <span className="gos-lr-chip"><CLIc name="building" size={13} /> {formData.account}</span> : null}
                {formData?.leadSource ? <span className="gos-lr-chip"><CLIc name="campaigns" size={13} /> {formData.leadSource}</span> : null}
              </div>
            </div>

            <div className={'gos-cl3-ready' + (allReady ? ' full' : '')}>
              <div className="gos-cl3-ready-top">
                <span className="gos-cl3-ready-label">Contact readiness</span>
                <span className="gos-cl3-ready-pct">{pct}%</span>
              </div>
              <div className="gos-cl3-bar"><div style={{ width: pct + '%' }} /></div>
              <div className={'gos-cl3-ready-sub' + (allReady ? ' ok' : '')}>
                {allReady ? (
                  <><CLIc name="checkCircle" size={13} /> All required fields complete</>
                ) : (
                  `${filledReq} of ${REQUIRED.length} required fields filled`
                )}
              </div>
            </div>
          </div>

          <ContactForm
            formData={formData}
            selectedOptions={selectedOptions}
            handleSubmit={handleSubmit}
            handleChangeForm={handleChangeForm}
            handleChangeCheckbox={handleChangeCheckbox}
            handleChange={handleChange}
            errormsg={errormsg}
            refs={refs}
          />
        </div>
      </div>
    </div>
  );
};

export default AddContactsForm;
